// Every Silver Service site, in the order the staff start page shows them.
// docs/site-map.md is the long form: what belongs where and why.
//
// All of them are one app; App.jsx picks the screen from the hostname in
// prod and from the #hash everywhere else (staging, localhost).

const DOMAIN = 'silverservice.com';

export const SITES = [
  {
    // Staff open Ordering at its own page, a look-only preview as a customer
    // of their choosing; plain orders.silverservice.com is the customer login.
    key: 'orders', host: 'orders', hash: '', staffHash: 'staff-preview', name: 'Ordering', for: 'Customers, and staff to look',
    tagline: 'Where customers order supplies.',
    does: ['Order guide, catalog and specials', 'Order history and invoices', 'Service and product requests', 'Staff: see it as any customer does, look-only'],
  },
  {
    key: 'manage', host: 'manage', hash: 'admin', name: 'Manage', for: 'Owners and office',
    tagline: 'How the ordering portal is set up.',
    does: ['Products, tier pricing and specials', 'Customer logins, delivery days, order guides and staff notes', 'New-customer applications, staff logins, receipts and exports'],
  },
  {
    key: 'sales', host: 'sales', hash: 'sales', name: 'Sales', for: 'Sales team and owners',
    tagline: 'How my accounts are doing, and who to call.',
    does: ['My accounts: spend, GP and the AI summary', 'Win-back, notes, calls, visits and follow-ups', 'Scorecard and website leads'],
  },
  {
    key: 'fulfillment', host: 'fulfillment', hash: 'fulfillment', name: 'Fulfillment', for: 'Office, warehouse, drivers and sales',
    tagline: 'Did the order arrive?',
    does: ['What goes out when, by route, FedEx or pickup', "Each order's stage: picked, shipped, invoiced, delivered", "Drivers' delivery records and orders to chase"],
  },
  {
    key: 'service', host: 'service', hash: 'service', name: 'Service desk', for: 'Technicians, office and sales',
    tagline: 'Whose machine needs us, and is it fixed?',
    does: ['Service requests: requested, scheduled, completed', "Each technician's day, with photos and notes", 'The machines at each customer and their history'],
  },
  {
    key: 'reports', host: 'reports', hash: 'reports', name: 'Reports', for: 'Olaf and Alet only',
    tagline: 'How is the business doing?',
    does: ['Sales by rep: spend, GP and quiet accounts', 'Business dashboard', 'Owner metrics: MRR, churn, slow movers', 'Bills: what each open QuickBooks bill really is'],
  },
  {
    key: 'purchase', host: 'purchase', hash: 'purchase', name: 'Purchasing', for: 'Owners and office',
    tagline: 'What to reorder from suppliers.',
    does: ['Stock levels and reorder suggestions', 'Purchase planning'],
  },
  {
    key: 'receipts', host: 'receipts', hash: 'receipt-capture', name: 'Receipts', for: 'Anyone with a company card',
    tagline: 'Photograph a company-card receipt.',
    does: ['Snap the receipt, say what it was for'],
  },
];

// "Where do I go for…?" — kept true to what is live today, so an entry
// changes when its screen moves to another site.
export const WHERE_TO_GO = [
  { task: 'Change a tier price', site: 'manage', where: 'Catalog & pricing → Tier Pricing' },
  { task: 'Put a product on special', site: 'manage', where: 'Catalog & pricing → Specials' },
  { task: 'Show, hide or fix a product', site: 'manage', where: 'Catalog & pricing' },
  { task: 'See the ordering site the way a customer does', site: 'orders', where: 'pick any customer' },
  { task: 'Invite a customer to the portal', site: 'manage', where: 'Customers' },
  { task: "Change a customer's delivery days", site: 'manage', where: 'Customers' },
  { task: 'Place an order for a customer', site: 'manage', where: 'Customers → Place order' },
  { task: 'Send a new customer the application form', site: 'manage', where: 'Customers → New Customers' },
  { task: 'Add a staff member or change their access', site: 'manage', where: 'Back office → Staff' },
  { task: 'See how an account is doing', site: 'sales' },
  { task: 'Log a call or visit, or set a follow-up', site: 'sales' },
  { task: 'Find accounts that stopped ordering', site: 'sales', where: 'Win-back' },
  { task: "Read a customer's AI summary or staff notes", site: 'sales', where: 'open the account' },
  { task: 'See revenue, GP and how each rep is doing', site: 'reports' },
  { task: "A customer's machine is down", site: 'service', where: 'Requested → Log a request' },
  { task: 'See what a technician is doing today', site: 'service', where: 'Scheduled / in progress' },
  { task: 'Record a machine at a customer', site: 'service', where: 'Equipment' },
  { task: 'Did an order ship, or arrive?', site: 'fulfillment', where: 'Find an order' },
  { task: "Where are my customers' orders?", site: 'fulfillment', where: 'Customers filter' },
  { task: 'Record a delivery, or print the run sheet', site: 'fulfillment', where: 'Deliveries' },
  { task: 'Orders shipped or closed but never invoiced', site: 'fulfillment', where: 'Problems' },
  { task: 'Reorder stock from a supplier', site: 'purchase' },
  { task: 'Hand in a company-card receipt', site: 'receipts' },
];

// Every site signs staff in at the start page; on Ordering that sign-in only
// opens the look-only customer preview.
export const STAFF_SITE_KEYS = SITES.map(s => s.key);

function onLiveDomain() {
  const host = (typeof window !== 'undefined' && window.location.hostname) || '';
  return SITES.some(s => host === `${s.host}.${DOMAIN}`) || host === `staff.${DOMAIN}`;
}

// The #hash is a page, then optionally "?" and parameters: "#sales?handoff=…"
// on staging. On a live host the hostname already names the page, so the hash
// can be parameters alone: "#handoff=…".
export function parseHash(hash = window.location.hash) {
  const raw = String(hash || '').replace(/^#/, '');
  const q = raw.indexOf('?');
  let page = q === -1 ? raw : raw.slice(0, q);
  let query = q === -1 ? '' : raw.slice(q + 1);
  if (page.includes('=')) {
    query = query ? `${page}&${query}` : page;
    page = '';
  }
  return { page, params: new URLSearchParams(query) };
}

function hashUrl(base, page, params) {
  const query = params ? new URLSearchParams(params).toString() : '';
  const hash = [page, query].filter(Boolean).join(page ? '?' : '');
  return hash ? `${base}/#${hash}` : base;
}

// `params` go in the #fragment, which the browser never sends to a server.
export function siteUrl(site, params) {
  if (onLiveDomain()) return hashUrl(`https://${site.host}.${DOMAIN}`, '', params);
  const url = hashUrl(window.location.origin, site.hash, params);
  return url === window.location.origin ? `${url}/` : url;
}

// Where staff open a site, and where the start page hands their sign-in
// back. The same as siteUrl except for Ordering, whose staff page is not the
// customers' front door.
export function staffSiteUrl(site, params) {
  if (!site.staffHash) return siteUrl(site, params);
  const base = onLiveDomain() ? `https://${site.host}.${DOMAIN}` : window.location.origin;
  return hashUrl(base, site.staffHash, params);
}

export function siteHostLabel(site) {
  return `${site.host}.${DOMAIN}`;
}

export function startPageUrl(params) {
  return onLiveDomain()
    ? hashUrl(`https://staff.${DOMAIN}`, '', params)
    : hashUrl(window.location.origin, 'staff', params);
}

export function siteByKey(key) {
  return SITES.find(s => s.key === key);
}
